import type { RouteLocation } from '#vue-router';
import type { RoutesNamesList } from '@typed-router';
import type { SportsBookSlug, SportsBookMenuItem } from '@/types/SportsBook';

import { AltenarSportsbook } from '@/shared/SportsBook/AltenarSportsbook';
import { CactusSportsbook } from '@/shared/SportsBook/CactusSportsbook';

const altenarSportsbookInstance = AltenarSportsbook.getInstance();

export const useSportsBook = () => {
  const {
    isBetByEnabled,
    isBetByTestModeEnabled,
    isAltenarEnabled,
    isAltenarTestModeEnabled,
    isCactusSportsbookEnabled,
  } = useFeatureFlagsEnv();

  const { isHomeAsSportbookEnabled } = useFeatureFlagsNew();

  const SPORTS_PAGE_NAME: RoutesNamesList = isHomeAsSportbookEnabled.value
    ? 'index'
    : 'sports-sports';

  const route = useRoute(SPORTS_PAGE_NAME);
  const router = useRouter();
  const { currentRouteName } = useRoutes();
  const { getSportBookData } = useSportsBookStore();
  const { menuItems } = storeToRefs(useSportsBookStore());

  const sportsPageBaseUrl = computed<string>(() => {
    return route.path.replace(/^\/sports/, '');
  });

  const fullSportsPageUrl = computed<string>(() => {
    return isHomeAsSportbookEnabled.value
      ? `https://${appUrl()}`
      : `https://${appUrl()}/sports`;
  });

  const isSportsPage = computed<boolean>(() => {
    if (currentRouteName.value === 'index') {
      return isHomeAsSportbookEnabled.value;
    }

    return currentRouteName.value === SPORTS_PAGE_NAME;
  });

  const isSportsHomePage = computed<boolean>(() => {
    if (!isSportsPage.value) {
      return false;
    }

    if (isBetByEnabled.value) {
      if (isHomeAsSportbookEnabled.value) {
        return false;
      }

      return !sportsPageBaseUrl.value || sportsPageBaseUrl.value === '/';
    }

    if (isAltenarEnabled.value) {
      return altenarSportsbookInstance.isSportsbookHomePage;
    }

    return !sportBookSlugFromUrl.value;
  });

  const hasSportBookActive = computed<boolean>(() => {
    return (
      isBetByEnabled.value ||
      isAltenarEnabled.value ||
      isCactusSportsbookEnabled.value
    );
  });

  const sportBookSlugFromUrl = computed<SportsBookSlug>(() => {
    const { sports } = route.params;

    return normalizeRouteParams(sports) as SportsBookSlug;
  });

  const championshipMenuItems = computed<SportsBookMenuItem[]>(() => {
    return menuItems.value
      .filter((sportBook: any) => {
        return sportBook.type === 'championship';
      })
      .map((sportBook: any) => {
        return {
          ...sportBook,
          isActive: isSportBookPageActive(sportBook.slug),
        };
      });
  });

  const sportsMenuItems = computed<SportsBookMenuItem[]>(() => {
    const filteredItems = menuItems.value.filter((sportBook: any) => {
      return sportBook.type === 'sport';
    });

    const firstFiveItems = filteredItems.slice(0, 5);

    return firstFiveItems.map((sportBook: any) => {
      return {
        ...sportBook,
        isActive: isSportBookPageActive(sportBook.slug),
      };
    });
  });

  function isSportBookPageActive(sportBookSlug: SportsBookSlug): boolean {
    if (!isSportsPage.value || !sportBookSlug) {
      return false;
    }

    const sportBookData = getSportBookData(sportBookSlug);

    const { path } = sportBookData;

    if (isBetByEnabled.value) {
      const { betby: betbyPath } = path;

      return isHomeAsSportbookEnabled.value
        ? false
        : sportsPageBaseUrl.value === betbyPath;
    }

    if (isAltenarEnabled.value) {
      return false;

      // const { altenar: altenarPath } = path;

      // return altenarSportsbookInstance.isSportBookPageActive(altenarPath);
    }

    if (isCactusSportsbookEnabled.value) {
      const cactusPath = path.cactus;

      if (cactusPath?.sportId) {
        return new RegExp(`/s-${cactusPath.sportId}(?:\\D|$)`).test(route.path);
      }

      if (cactusPath?.masterLeagueId) {
        return new RegExp(
          `/(?:ml|l)-${cactusPath.masterLeagueId}(?:\\D|$)`,
        ).test(route.path);
      }
    }

    return sportBookSlugFromUrl.value === sportBookSlug;
  }

  function saniziteSlugUrlInBetby(btPath: string) {
    if (!sportBookSlugFromUrl.value) {
      return;
    }

    if (
      // @ts-ignore
      (sportBookSlugFromUrl.value === 'betby' &&
        isBetByTestModeEnabled.value) || // @ts-ignore
      (sportBookSlugFromUrl.value === 'altenar' &&
        isAltenarTestModeEnabled.value)
    ) {
      return;
    }

    return navigateTo(
      isHomeAsSportbookEnabled.value ? '/' + btPath : '/sports' + btPath,
      { replace: true },
    );
  }

  function betByRedirectToUrl(url: string): void {
    if (!url) {
      return;
    }

    if (!window.btRender) {
      return;
    }

    saniziteSlugUrlInBetby(url);

    window.btRender?.updateOptions({
      url,
    });
  }

  async function goToSportsHomePage() {
    const route = router.resolve({ name: SPORTS_PAGE_NAME });

    if (!route) {
      return;
    }

    if (isBetByEnabled.value) {
      betByRedirectToUrl('/');
    }

    if (isAltenarEnabled.value) {
      return goToSportBookPage('upcoming');
    }

    if (isCactusSportsbookEnabled.value) {
      return await navigateTo({
        path: route.fullPath,
        query: { tab: 'Early' },
      });
    }

    return await navigateTo(route.fullPath);
  }

  function goToSportBookPage(sportBookSlug: SportsBookSlug) {
    if (isAltenarEnabled.value) {
      console.debug('altenar: goToSportBookPage', sportBookSlug);
    }

    const sportBookData = getSportBookData(sportBookSlug);

    const { path } = sportBookData;
    const { betby: betbyPath, cactus: cactusPath, altenar: altenarPath } = path;

    const route = router.resolve({ name: SPORTS_PAGE_NAME });

    if (!route?.name) {
      return new Error('goToSportBookPage: name param is not available');
    }

    const to = {} as RouteLocation;

    if (isCactusSportsbookEnabled.value) {
      if (cactusPath?.['betting-history']) {
        to.path = `/sports/my-bets`;
      }

      if (cactusPath?.sportId) {
        to.path = `/sports/${sportBookSlug}/s-${cactusPath.sportId}`;
      }

      if (cactusPath?.masterLeagueId) {
        to.path = `/sports/${sportBookSlug}/ml-${cactusPath.masterLeagueId}`;
      }

      if (cactusPath?.tab || cactusPath?.eventlistTab) {
        if (sportBookSlug === 'live' || sportBookSlug === 'upcoming') {
          to.path = `/sports`;
        } else {
          to.path = `/sports/${sportBookSlug}`;
        }

        to.query = { tab: cactusPath.tab || cactusPath.eventlistTab };
      }

      return navigateTo(to);
    }

    if (isBetByEnabled.value && betbyPath) {
      // to.path = isHomeAsSportbookEnabled.value
      //   ? '/' + betbyPath
      //   : '/sports' + betbyPath;

      to.query = { 'bt-path': betbyPath };
      betByRedirectToUrl(betbyPath);

      return navigateTo(to);
    }

    if (isAltenarEnabled.value && altenarPath) {
      to.path = altenarSportsbookInstance.getReadableUrl(
        altenarPath,
        sportBookSlug,
      );

      return navigateTo(to);
    }
  }

  const searchSports = async (params: any) => {
    try {
      const { ApiService } = useApiService();

      const url =
        '/cactus-sportbook/search?' + new URLSearchParams(params).toString();

      const response = await ApiService.get(url);

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar esportes:', error);
      throw error;
    }
  };

  function getLeagueImage(league_name: string) {
    // Normalize league name to lowercase and remove accents for easier matching
    const normalizedName = league_name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036F]/g, '');

    // Define mapping of keywords to flags/ codes
    const leagueFlags = {
      brasil: 'BRA',
      brazil: 'BRA',
      argentina: 'ARG',
      canada: 'CAN',
      china: 'CHN',
      croacia: 'HRV',
      bolivia: 'BOL',
      dinamarca: 'DNK',
      eslovaquia: 'SVK',
      filipinas: 'PHL',
      grecia: 'GRC',
      greece: 'GRC',
      japao: 'JPN',
      inglaterra: 'ENG',
      peru: 'PER',
      'la liga': 'ESP',
      espanha: 'ESP',
      spain: 'ESP',
      espanhola: 'ESP',
      fnl: 'RUS',
      russia: 'RUS',
      franca: 'FRA',
      alemanha: 'DEU',
      paraguai: 'PRY',
      paraguay: 'PRY',
      portugal: 'PRT',
      romenia: 'ROU',
      suecia: 'SWE',
      serbia: 'SRB',
      servia: 'SRB',
      venezuela: 'VEN',
      noruega: 'NOR',
      mexico: 'MEX',
      nba: 'USA',
      eua: 'USA',
      mls: 'USA',
      concacaf: 'CONCACAF',
      libertadores: 'LIBERTA',
      'liga dos campeoes': 'UEFA',
      'clubes da fifa': 'FIFA2025',
      'copa do mundo': 'Football',
      'e-soccer': 'EFootball',
    };

    // Check regular flag mappings
    for (const [key, value] of Object.entries(leagueFlags)) {
      if (normalizedName.includes(key)) {
        return `flags/${value}`;
      }
    }

    // Default fallback
    return 'calendar';
  }

  function getSportImage(sport_id: string): string {
    const sportImages = {
      '1': 'widgets/Football',
      '2': 'widgets/Basketball',
      '3': 'widgets/Rugby',
      '11': 'widgets/Rugby',
      '35': 'widgets/Rugby',
      '41': 'widgets/Rugby',
      '34': 'widgets/Badminton',
      '5': 'widgets/Tennis',
      '6': 'widgets/Tennis',
      '7': 'widgets/Baseball',
      '8': 'widgets/IceHockey',
      '19': 'widgets/Volleyball',
      '20': 'widgets/Boxe',
      '25': 'widgets/Futsal',
      '15': 'widgets/Darts',
      '221': 'widgets/Dota2',
      '26': 'widgets/Pingpong',
      '230': 'widgets/CounterStrike',
      '228': 'widgets/Valorant',
      'V-Soccer': 'widgets/Football',
    } as const;

    return (
      sportImages[sport_id as keyof typeof sportImages] ?? 'widgets/Default'
    );
  }

  function getEventDate(event_date: string) {
    const date = new Date(event_date);
    const today = new Date();
    const tomorrow = new Date(today);

    tomorrow.setDate(tomorrow.getDate() + 1);

    // Reset hours to compare just the dates
    const dateWithoutTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    const todayWithoutTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const tomorrowWithoutTime = new Date(
      tomorrow.getFullYear(),
      tomorrow.getMonth(),
      tomorrow.getDate(),
    );

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const time = `${hours}:${minutes}`;

    const { t } = useTexts();

    if (dateWithoutTime.getTime() === todayWithoutTime.getTime()) {
      return `${t('general.today')} ${time}`;
    }

    if (dateWithoutTime.getTime() === tomorrowWithoutTime.getTime()) {
      return `${t('general.tomorrow')} ${time}`;
    }

    return (
      date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      }) + ` ${time}`
    );
  }

  const isCactusSportsbookActive = computed<boolean>(() => {
    return isCactusSportsbookEnabled.value && isSportsPage.value;
  });

  function openBettingRulesInSportsbook(): void {
    CactusSportsbook.getInstance().showBettingRules();
  }

  return {
    isSportsPage,
    isSportsHomePage,
    hasSportBookActive,
    sportBookSlugFromUrl,
    sportsPageBaseUrl,
    championshipMenuItems,
    sportsMenuItems,
    isSportBookPageActive,
    saniziteSlugUrlInBetby,
    betByRedirectToUrl,
    getSportBookData,
    goToSportsHomePage,
    goToSportBookPage,
    searchSports,
    SPORTS_PAGE_NAME,
    fullSportsPageUrl,
    getLeagueImage,
    getSportImage,
    getEventDate,
    isCactusSportsbookActive,
    openBettingRulesInSportsbook,
  };
};
